import { useState } from 'react';
import { Input, Select, Checkbox, Icon, DataGrid, FileDrop, Button } from '@y365/design-system';
import { formatAbn, isUnknown, unknownValue, type FieldDefinition } from '@y365/shared';

interface Props {
  field: FieldDefinition;
  path: string;
  value: unknown;
  error?: string;
  onChange: (value: unknown) => void;
  /**
   * Only `externalAction` needs this: its sub-fields have their own paths
   * (`group.field.subField`) and therefore their own errors, which a single
   * `error` string cannot carry.
   */
  errorsByPath?: Map<string, string>;
}

type GridRow = Record<string, unknown>;

/**
 * Grid entry, with file upload as an equal alternative.
 *
 * Never make the grid the only path. Someone on a screen reader will struggle
 * with a spreadsheet widget however well it is built, and the departing IT
 * provider filling this in is often unmotivated — a grid they cannot face means
 * no data at all. The upload is the escape hatch that keeps the section
 * completable.
 */
function GridField({ field, path, value, error, onChange }: Props) {
  const [mode, setMode] = useState<'grid' | 'upload'>('grid');
  const [files, setFiles] = useState<File[]>([]);

  const rows: GridRow[] = Array.isArray(value) ? (value as GridRow[]) : [];

  return (
    <div>
      <div
        style={{
          display: 'flex',
          gap: 'var(--sp-2)',
          marginBottom: 'var(--sp-4)',
          flexWrap: 'wrap',
        }}
      >
        <Button
          type="button"
          size="sm"
          variant={mode === 'grid' ? 'primary' : 'secondary'}
          data-y365-focusable
          aria-pressed={mode === 'grid'}
          onClick={() => setMode('grid')}
        >
          Type or paste a list
        </Button>
        <Button
          type="button"
          size="sm"
          variant={mode === 'upload' ? 'primary' : 'secondary'}
          data-y365-focusable
          aria-pressed={mode === 'upload'}
          onClick={() => setMode('upload')}
        >
          Upload a file instead
        </Button>
      </div>

      {mode === 'grid' ? (
        <DataGrid
          id={`field-${path}`}
          label={field.label}
          hint={field.hint}
          error={error}
          columns={field.columns ?? []}
          value={rows}
          onChange={(next) => onChange(next)}
        />
      ) : (
        <FileDrop
          id={`field-${path}`}
          label={field.label}
          hint="Excel or CSV, up to 10MB. We will read it and come back to you if anything is unclear."
          files={files}
          onFiles={(added) => setFiles((prev) => [...prev, ...added])}
          onRemove={(index) => setFiles((prev) => prev.filter((_, i) => i !== index))}
        />
      )}
    </div>
  );
}

/**
 * An action that happens somewhere else, and the record that it happened.
 *
 * Read docs/microsoft-integration.md before touching this. We are an indirect
 * reseller: we cannot attest to Microsoft Customer Agreement acceptance, and
 * nothing entered here constitutes acceptance of it. So this must never look
 * like a signature control or an "I agree" tickbox — it is a link out, and
 * afterwards a record of what the customer did over there.
 *
 * There is deliberately no "I have done this" checkbox. The signer details are
 * the evidence; a tick on its own is worth nothing to our provider and is
 * exactly the self-asserted consent the design rejects for GDAP.
 */
function ExternalActionField({ field, path, value, onChange, errorsByPath }: Props) {
  const recorded = (value ?? {}) as Record<string, unknown>;
  const sub = field.actionFields ?? [];

  const setSub = (key: string, next: unknown) => onChange({ ...recorded, [key]: next });

  return (
    <div>
      <p style={{ margin: 0, marginBottom: 'var(--sp-3)', fontFamily: 'var(--font-body)' }}>
        <a
          href={field.url}
          target="_blank"
          rel="noopener noreferrer"
          data-y365-focusable
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 'var(--sp-2)',
            color: 'var(--text-link)',
            fontFamily: 'var(--font-body)',
            fontWeight: 'var(--fw-bold)',
          }}
        >
          {field.linkLabel ?? 'Open'}
          <Icon name="external-link" size={16} />
        </a>
      </p>

      {field.hint && (
        <p
          style={{
            margin: '0 0 var(--sp-4)',
            fontFamily: 'var(--font-body)',
            fontSize: 'var(--fs-xs)',
            color: 'var(--text-muted)',
          }}
        >
          {field.hint}
        </p>
      )}

      {/* A fieldset, so a screen reader hears these fields as one thing that
          belongs to the action above rather than five loose inputs. */}
      <fieldset
        style={{
          border: '1px solid var(--border-subtle)',
          borderRadius: 'var(--radius-md)',
          padding: 'var(--sp-4)',
          margin: 0,
        }}
      >
        <legend
          style={{
            padding: '0 var(--sp-2)',
            fontFamily: 'var(--font-body)',
            fontSize: 'var(--fs-sm)',
            fontWeight: 'var(--fw-bold)',
          }}
        >
          Once you have done it, tell us who and when
        </legend>

        <p
          style={{
            margin: '0 0 var(--sp-4)',
            fontFamily: 'var(--font-body)',
            fontSize: 'var(--fs-xs)',
            color: 'var(--text-muted)',
          }}
        >
          Leave this blank if you have not done it yet — you can come back to it on the same link.
        </p>

        {sub.map((subField) => {
          const subPath = `${path}.${subField.key}`;
          return (
            <div key={subPath} style={{ marginBottom: 'var(--sp-4)' }}>
              <FieldRenderer
                field={subField}
                path={subPath}
                value={recorded[subField.key]}
                error={errorsByPath?.get(subPath)}
                onChange={(v) => setSub(subField.key, v)}
              />
            </div>
          );
        })}
      </fieldset>
    </div>
  );
}

/**
 * Renders one field from the frozen definition.
 *
 * Everything comes from the design system — no ad-hoc inputs. Where the design
 * system lacks a component (textarea), it is built here to the same visual
 * language and should be lifted upstream: see packages/design-system/VENDORED.md.
 */
export function FieldRenderer({ field, path, value, error, onChange, errorsByPath }: Props) {
  const id = `field-${path}`;
  const unknown = isUnknown(value);

  // The "I don't know" escape hatch. In the IT section this is the difference
  // between an honest gap and a fabricated answer, and a fabricated answer is
  // worse — we would act on it.
  const unknownToggle = field.allowUnknown ? (
    <div style={{ marginTop: 'var(--sp-2)' }}>
      <Checkbox
        checked={unknown}
        onChange={(checked) => onChange(checked ? unknownValue() : '')}
        label={
          <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-muted)' }}>
            I don&rsquo;t know this
          </span>
        }
      />
    </div>
  ) : null;

  if (unknown) {
    return (
      <div>
        <label
          htmlFor={id}
          style={{
            display: 'block',
            marginBottom: 'var(--sp-2)',
            fontFamily: 'var(--font-body)',
            fontSize: 'var(--fs-sm)',
            fontWeight: 'var(--fw-bold)',
            color: 'var(--text-strong)',
          }}
        >
          {field.label}
        </label>
        <div
          id={id}
          tabIndex={-1}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--sp-2)',
            height: 44,
            padding: '0 var(--sp-4)',
            borderRadius: 'var(--radius-sm)',
            border: '1.5px dashed var(--border-default)',
            background: 'var(--bg-inset)',
            color: 'var(--text-muted)',
            fontFamily: 'var(--font-body)',
            fontSize: 'var(--fs-body)',
          }}
        >
          <Icon name="info" size={16} />
          Marked as unknown — that is fine, we will find out
        </div>
        {unknownToggle}
      </div>
    );
  }

  const str = typeof value === 'string' ? value : '';

  const common = {
    id,
    label: field.label,
    hint: field.hint,
    error,
    required: field.required,
  };

  switch (field.type) {
    case 'textarea':
      return (
        <div>
          <label
            htmlFor={id}
            style={{
              display: 'block',
              marginBottom: 'var(--sp-2)',
              fontFamily: 'var(--font-body)',
              fontSize: 'var(--fs-sm)',
              fontWeight: 'var(--fw-bold)',
              color: 'var(--text-strong)',
            }}
          >
            {field.label}
          </label>
          <textarea
            id={id}
            value={str}
            maxLength={field.maxLength}
            placeholder={field.placeholder}
            aria-invalid={error ? 'true' : undefined}
            aria-describedby={error || field.hint ? `${id}-desc` : undefined}
            onChange={(e) => onChange(e.target.value)}
            rows={4}
            style={{
              width: '100%',
              padding: 'var(--sp-3) var(--sp-4)',
              background: 'var(--bg-surface)',
              border: `1.5px solid ${error ? 'var(--y-danger)' : 'var(--border-default)'}`,
              borderRadius: 'var(--radius-sm)',
              fontFamily: 'var(--font-body)',
              fontSize: 'var(--fs-body)',
              color: 'var(--text-strong)',
              resize: 'vertical',
              outline: 'none',
            }}
            data-y365-focusable
          />
          {(error || field.hint) && (
            <span
              id={`${id}-desc`}
              style={{
                display: 'block',
                marginTop: 'var(--sp-2)',
                fontFamily: 'var(--font-body)',
                fontSize: 'var(--fs-xs)',
                color: error ? 'var(--y-danger)' : 'var(--text-muted)',
              }}
            >
              {error || field.hint}
            </span>
          )}
          {unknownToggle}
        </div>
      );

    case 'choice':
      return (
        <div>
          <Select
            {...common}
            placeholder="Choose one"
            options={field.options ?? []}
            value={str}
            onChange={(e) => onChange(e.target.value)}
          />
          {unknownToggle}
        </div>
      );

    case 'abn':
      return (
        <div>
          <Input
            {...common}
            value={str}
            inputMode="numeric"
            placeholder={field.placeholder}
            onChange={(e) => onChange(e.target.value)}
            // Format on blur rather than while typing: reformatting mid-entry
            // moves the caret and is genuinely unpleasant to type into.
            onBlur={(e) => {
              const formatted = formatAbn(e.target.value);
              if (formatted !== e.target.value) onChange(formatted);
            }}
          />
          {unknownToggle}
        </div>
      );

    case 'email':
      return (
        <div>
          <Input {...common} type="email" autoComplete="email" value={str}
            onChange={(e) => onChange(e.target.value)} />
          {unknownToggle}
        </div>
      );

    case 'phone':
      return (
        <div>
          <Input {...common} type="tel" autoComplete="tel" value={str}
            onChange={(e) => onChange(e.target.value)} />
          {unknownToggle}
        </div>
      );

    case 'date':
      return (
        <div>
          <Input {...common} type="date" value={str} onChange={(e) => onChange(e.target.value)} />
          {unknownToggle}
        </div>
      );

    case 'number':
      return (
        <div>
          <Input
            {...common}
            type="number"
            value={typeof value === 'number' ? String(value) : ''}
            onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
          />
          {unknownToggle}
        </div>
      );

    case 'multichoice': {
      const selected = Array.isArray(value) ? (value as string[]) : [];
      const options = field.options ?? [];

      /*
        A fieldset with a legend, not a bare stack of checkboxes. Without it a
        screen reader reads "Main contact, checkbox" with no idea what question
        it answers — and in a repeatable group there are three identical sets on
        the page.
      */
      return (
        <fieldset
          id={id}
          tabIndex={-1}
          style={{ border: 'none', margin: 0, padding: 0, minInlineSize: 'auto' }}
          aria-describedby={error || field.hint ? `${id}-desc` : undefined}
        >
          <legend
            style={{
              padding: 0,
              marginBottom: 'var(--sp-2)',
              fontFamily: 'var(--font-body)',
              fontSize: 'var(--fs-sm)',
              fontWeight: 'var(--fw-bold)',
              color: 'var(--text-strong)',
            }}
          >
            {field.label}
          </legend>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--sp-3)',
              padding: 'var(--sp-3) var(--sp-4)',
              borderRadius: 'var(--radius-sm)',
              border: `1.5px solid ${error ? 'var(--y-danger)' : 'var(--border-default)'}`,
              background: 'var(--bg-surface)',
            }}
          >
            {options.map((option) => (
              <Checkbox
                key={option.value}
                checked={selected.includes(option.value)}
                label={option.label}
                onChange={(checked) => {
                  // Rebuild from the option order rather than pushing, so the
                  // stored order is stable no matter what order they tick.
                  const next = options
                    .map((o) => o.value)
                    .filter((v) =>
                      v === option.value ? checked : selected.includes(v),
                    );
                  onChange(next);
                }}
              />
            ))}
          </div>

          {(error || field.hint) && (
            <span
              id={`${id}-desc`}
              style={{
                display: 'block',
                marginTop: 'var(--sp-2)',
                fontFamily: 'var(--font-body)',
                fontSize: 'var(--fs-xs)',
                color: error ? 'var(--y-danger)' : 'var(--text-muted)',
              }}
            >
              {error || field.hint}
            </span>
          )}
          {unknownToggle}
        </fieldset>
      );
    }

    case 'grid':
      return (
        <GridField field={field} path={path} value={value} error={error} onChange={onChange} />
      );

    /*
      A statement the customer agrees to.

      The full statement is the checkbox's own label rather than a heading above
      it, so what is being agreed to and the act of agreeing are one control —
      a screen reader announces the whole sentence when it lands on the box,
      and there is no way to tick something whose text you skipped past.
    */
    case 'consent':
      return (
        <div>
          <Checkbox
            checked={value === true}
            onChange={(checked) => onChange(checked ? true : undefined)}
            label={
              <span style={{ fontFamily: 'var(--font-body)', color: 'var(--text-strong)' }}>
                {field.label}
              </span>
            }
          />
          {(error || field.hint) && (
            <p
              style={{
                margin: 'var(--sp-2) 0 0',
                fontFamily: 'var(--font-body)',
                fontSize: 'var(--fs-xs)',
                color: error ? 'var(--y-danger)' : 'var(--text-muted)',
              }}
            >
              {error || field.hint}
            </p>
          )}
        </div>
      );

    case 'externalAction':
      return (
        <ExternalActionField
          field={field}
          path={path}
          value={value}
          error={error}
          onChange={onChange}
          errorsByPath={errorsByPath}
        />
      );

    case 'link': {
      /*
        A link's href can be per-customer, in which case the template ships it
        empty and the resolved definition's prefill carries the real one — that
        is how the GDAP approval URL arrives, minted at send time from the Graph
        relationship id. Prefill lands in values like any other field, so the
        value wins over the template's static url when there is one.

        Nothing renders if neither exists. An <a href=""> reloads the page,
        which for the one link that grants us administrative access would be a
        genuinely bad outcome: the customer clicks, nothing happens, and they
        report it done.
      */
      const href = (typeof value === 'string' && value.trim()) || field.url;

      if (!href) {
        return (
          <div>
            <p style={{ margin: 0, marginBottom: 'var(--sp-2)', fontFamily: 'var(--font-body)' }}>
              {field.label}
            </p>
            <p
              style={{
                margin: 0,
                fontFamily: 'var(--font-body)',
                fontSize: 'var(--fs-sm)',
                color: 'var(--text-muted)',
              }}
            >
              We are still preparing this link. You do not need to wait for it — finish the rest and
              we will be in touch.
            </p>
          </div>
        );
      }

      return (
        <div>
          <p style={{ margin: 0, marginBottom: 'var(--sp-2)', fontFamily: 'var(--font-body)' }}>
            {field.label}
          </p>
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            data-y365-focusable
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 'var(--sp-2)',
              color: 'var(--text-link)',
              fontFamily: 'var(--font-body)',
              fontWeight: 'var(--fw-bold)',
            }}
          >
            {field.linkLabel ?? 'Open'}
            <Icon name="external-link" size={16} />
          </a>
          {field.hint && (
            <p style={{ margin: 'var(--sp-2) 0 0', fontSize: 'var(--fs-xs)', color: 'var(--text-muted)' }}>
              {field.hint}
            </p>
          )}
        </div>
      );
    }

    default:
      return (
        <div>
          <Input
            {...common}
            value={str}
            maxLength={field.maxLength}
            placeholder={field.placeholder}
            onChange={(e) => onChange(e.target.value)}
          />
          {unknownToggle}
        </div>
      );
  }
}
